import {
  NotificationRequestType,
  NotificationScheduleFrequency,
} from '../types/notifications.types';
import { Language } from '../types/shared.types';

/**
 * Static capabilities of each notification type (notifications.md §5).
 *
 * - `schedulable`   — the manager can pick a D/W/M schedule (FR-05/FR-08).
 * - `event_driven`  — fires as domain events happen (FR-05).
 * - `customer_copy` — the verified customer/debtor also receives a copy
 *                     (FR-03/FR-04).
 * - `fixed_frequency` — when set, the schedule frequency cannot be changed
 *                     (debt reminders must scan daily to catch T−3 / weekly
 *                     overdue tiers, FR-16).
 * - `assignee_only`  — sent to the people the request names
 *                     (`recipient_employee_ids`), never to subscribers, and
 *                     off until a manager turns it on for the store.
 */
export interface NotificationTypeCapability {
  schedulable: boolean;
  event_driven: boolean;
  customer_copy: boolean;
  fixed_frequency?: NotificationScheduleFrequency;
  assignee_only?: boolean;
}

export const NOTIFICATION_TYPE_CAPABILITIES: Record<
  NotificationRequestType,
  NotificationTypeCapability
> = {
  [NotificationRequestType.BUSINESS_SUMMARY]: {
    schedulable: true,
    event_driven: false,
    customer_copy: false,
  },
  [NotificationRequestType.SHIFT_REPORT]: {
    schedulable: false,
    event_driven: true,
    customer_copy: false,
  },
  [NotificationRequestType.LOW_STOCK]: {
    schedulable: true,
    event_driven: false,
    customer_copy: false,
  },
  [NotificationRequestType.EXPIRING_PRODUCTS]: {
    schedulable: true,
    event_driven: false,
    customer_copy: false,
    fixed_frequency: NotificationScheduleFrequency.DAILY,
  },
  [NotificationRequestType.INVENTORY_COUNT_SUMMARY]: {
    schedulable: true,
    event_driven: false,
    customer_copy: false,
  },
  [NotificationRequestType.PRICE_CHANGES]: {
    // Either as it happens (no schedule) or batched on a schedule.
    schedulable: true,
    event_driven: true,
    customer_copy: false,
  },
  [NotificationRequestType.PURCHASE_ORDER_CHANGES]: {
    schedulable: false,
    event_driven: true,
    customer_copy: false,
  },
  [NotificationRequestType.RECEIPT]: {
    schedulable: false,
    event_driven: true,
    customer_copy: true,
  },
  [NotificationRequestType.DEBTS]: {
    schedulable: false,
    event_driven: true,
    customer_copy: true,
  },
  [NotificationRequestType.DEBT_PAYMENT_REMINDER]: {
    schedulable: true,
    event_driven: false,
    customer_copy: true,
    fixed_frequency: NotificationScheduleFrequency.DAILY,
  },
  [NotificationRequestType.EXPENSE]: {
    schedulable: false,
    event_driven: true,
    customer_copy: false,
  },
  [NotificationRequestType.INVENTORY_UPDATE_FAILED]: {
    schedulable: false,
    event_driven: true,
    customer_copy: false,
  },
  [NotificationRequestType.AVAILABLE_MONEY]: {
    schedulable: true,
    event_driven: false,
    customer_copy: false,
  },
  [NotificationRequestType.INVOICE]: {
    schedulable: false,
    event_driven: true,
    customer_copy: false,
  },
  [NotificationRequestType.TOPUP]: {
    schedulable: false,
    event_driven: true,
    customer_copy: false,
  },
  [NotificationRequestType.TASK_ASSIGNED]: {
    schedulable: false,
    event_driven: true,
    customer_copy: false,
    assignee_only: true,
  },
};

/** Display order of the types in settings and history screens. */
export const NOTIFICATION_TYPE_ORDER: NotificationRequestType[] = [
  NotificationRequestType.BUSINESS_SUMMARY,
  NotificationRequestType.SHIFT_REPORT,
  NotificationRequestType.LOW_STOCK,
  NotificationRequestType.EXPIRING_PRODUCTS,
  NotificationRequestType.INVENTORY_COUNT_SUMMARY,
  NotificationRequestType.INVENTORY_UPDATE_FAILED,
  NotificationRequestType.PRICE_CHANGES,
  NotificationRequestType.PURCHASE_ORDER_CHANGES,
  NotificationRequestType.RECEIPT,
  NotificationRequestType.DEBTS,
  NotificationRequestType.DEBT_PAYMENT_REMINDER,
  NotificationRequestType.EXPENSE,
  NotificationRequestType.AVAILABLE_MONEY,
  NotificationRequestType.INVOICE,
  NotificationRequestType.TOPUP,
  NotificationRequestType.TASK_ASSIGNED,
];

/** Human-readable type names per language (WhatsApp template variable {{1}}). */
export const NOTIFICATION_TYPE_LABELS: Record<
  NotificationRequestType,
  Record<Language, string>
> = {
  [NotificationRequestType.BUSINESS_SUMMARY]: {
    [Language.EN_US]: 'Sales Report',
    [Language.PT_PT]: 'Relatório de Vendas',
  },
  [NotificationRequestType.SHIFT_REPORT]: {
    [Language.EN_US]: 'Shift Closed',
    [Language.PT_PT]: 'Turno Fechado',
  },
  [NotificationRequestType.LOW_STOCK]: {
    [Language.EN_US]: 'Low Stock Alert',
    [Language.PT_PT]: 'Alerta de Stock Baixo',
  },
  [NotificationRequestType.EXPIRING_PRODUCTS]: {
    [Language.EN_US]: 'Expiring Products',
    [Language.PT_PT]: 'Produtos a Expirar',
  },
  [NotificationRequestType.INVENTORY_COUNT_SUMMARY]: {
    [Language.EN_US]: 'Inventory Count',
    [Language.PT_PT]: 'Contagem de Inventário',
  },
  [NotificationRequestType.PRICE_CHANGES]: {
    [Language.EN_US]: 'Price Changes',
    [Language.PT_PT]: 'Alterações de Preços',
  },
  [NotificationRequestType.PURCHASE_ORDER_CHANGES]: {
    [Language.EN_US]: 'Purchase Order',
    [Language.PT_PT]: 'Ordem de Compra',
  },
  [NotificationRequestType.RECEIPT]: {
    [Language.EN_US]: 'Receipt',
    [Language.PT_PT]: 'Recibo',
  },
  [NotificationRequestType.DEBTS]: {
    [Language.EN_US]: 'Debt Update',
    [Language.PT_PT]: 'Atualização de Dívida',
  },
  [NotificationRequestType.DEBT_PAYMENT_REMINDER]: {
    [Language.EN_US]: 'Payment Reminder',
    [Language.PT_PT]: 'Lembrete de Pagamento',
  },
  [NotificationRequestType.EXPENSE]: {
    [Language.EN_US]: 'Expense',
    [Language.PT_PT]: 'Despesa',
  },
  [NotificationRequestType.INVENTORY_UPDATE_FAILED]: {
    [Language.EN_US]: 'Stock Update Failed',
    [Language.PT_PT]: 'Falha ao Atualizar Stock',
  },
  [NotificationRequestType.AVAILABLE_MONEY]: {
    [Language.EN_US]: 'Available Money',
    [Language.PT_PT]: 'Dinheiro Disponível',
  },
  [NotificationRequestType.INVOICE]: {
    [Language.EN_US]: 'Invoice',
    [Language.PT_PT]: 'Fatura',
  },
  [NotificationRequestType.TOPUP]: {
    [Language.EN_US]: 'Topup',
    [Language.PT_PT]: 'Recarga',
  },
  [NotificationRequestType.TASK_ASSIGNED]: {
    [Language.EN_US]: 'Urgent Task',
    [Language.PT_PT]: 'Tarefa Urgente',
  },
};

/** Short manager-facing descriptions for the settings screen (English). */
export const NOTIFICATION_TYPE_DESCRIPTIONS: Record<
  NotificationRequestType,
  string
> = {
  [NotificationRequestType.BUSINESS_SUMMARY]:
    'Sales, refunds, profit and breakdown by payment method and employee for the period.',
  [NotificationRequestType.SHIFT_REPORT]:
    'Sent when a shift is closed: duration, sales, cash and mobile money counts and differences.',
  [NotificationRequestType.LOW_STOCK]:
    'Items at or below their low stock threshold.',
  [NotificationRequestType.EXPIRING_PRODUCTS]:
    'Daily: products in stock whose purchase-order expiry date is close, with the order and the date.',
  [NotificationRequestType.INVENTORY_COUNT_SUMMARY]:
    'Summary of inventory counts and corrections for the period.',
  [NotificationRequestType.PRICE_CHANGES]:
    'Changed prices — as they happen, or batched on a schedule.',
  [NotificationRequestType.PURCHASE_ORDER_CHANGES]:
    'Sent when a purchase order is created, updated, received or cancelled.',
  [NotificationRequestType.RECEIPT]:
    'Receipt copy sent to the verified customer; subscribed employees also get a copy.',
  [NotificationRequestType.DEBTS]:
    'Debt created and debt payment confirmations to the verified customer and subscribers.',
  [NotificationRequestType.DEBT_PAYMENT_REMINDER]:
    'Payment reminder 3 days before the due date and weekly overdue notices while unpaid.',
  [NotificationRequestType.EXPENSE]: 'Sent when an expense is recorded.',
  [NotificationRequestType.INVENTORY_UPDATE_FAILED]:
    'Sent when a stock change (sale, purchase order, count…) could not be recorded: who, when, what, the quantity and why.',
  [NotificationRequestType.AVAILABLE_MONEY]:
    'Balances per employee and payment method at the scheduled time.',
  [NotificationRequestType.INVOICE]:
    'Sent when a ShopNSmile invoice is issued.',
  [NotificationRequestType.TOPUP]: 'Sent when a topup is received and claimed.',
  [NotificationRequestType.TASK_ASSIGNED]:
    'Sent to the person (or everyone in the role) an urgent task is assigned to. Off until you turn it on.',
};

export function notificationTypeLabel(
  type: NotificationRequestType,
  language: Language = Language.EN_US,
): string {
  return (
    NOTIFICATION_TYPE_LABELS[type]?.[language] ??
    NOTIFICATION_TYPE_LABELS[type]?.[Language.EN_US] ??
    type
  );
}
